<template lang="pug">
div
  .heading.mb-M
    h2 Custom filters
    BriqButton(variant="main" small ref="addCustomFilter" @click="addCustomFilter()" v-show="!showTranslations" data-test-id="add-filter") Add filter
  .custom-filters-filter(v-for="(filter, filterKey) in filters")
    .custom-filters-filter-container
      BriqInput.custom-filters-filter-label(
        label="Filter label" 
        required 
        v-model="filter.name"
        :error="!showTranslations ? customFilterError(filterKey) : null"
        @blur="$v.filters.$each[filterKey].name.$touch()"
      )
      .custom-filters-delete(@click="removeCustomFilter(filterKey)" ref="removeCustomFilter" v-show="!showTranslations")
        BriqIcon(name="icon-input-trash-icon")
    div
      BriqInput(label="Filter question"  v-model="filter.question")
    div
      .custom-filters-filter-row(v-for="(filterRow, rowKey) in filter.rows" )
        BriqCheckbox.custom-filters-checkbox(label="Show" v-model="filterRow.active" @input="updateShowForTranslationInfoObject(filterKey, rowKey, filterRow.active)" v-show="!showTranslations")
        BriqInput.custom-filters-filter-row-name(
          label="Option label" 
          required
          v-model="filterRow.name" 
          :disabled="!rowKey.includes(`new`) && !showTranslations"
          :error="!showTranslations ? customFilterRowError(filterKey, rowKey) : null"
          @blur="$v.filters.$each[filterKey].rows.$each[rowKey].name.$touch()"
        )
        .custom-filters-row-button(
          v-show="!showTranslations"
          :class="rowKey === '1' || rowKey === 'new-1' ? `disabled` : ``"
          @click="removeCustomFilterRow(filterKey, rowKey)"
          )
          BriqIcon(name="icon-input-trash-icon")
        .custom-filters-row-button(ref="addCustomFilterRow" @click="addCustomFilterRow(filterKey)"  v-show="!showTranslations")
          BriqIcon(name="icon-input-plus-icon")
</template>

<script>
import Vue from 'vue'
import { required } from 'vuelidate/lib/validators'
import { extractNumberFromString } from '@v/util/extractNumberFromString.js'
import translationInfoHelperFunctionsMixin from '@v/util/translationInfoHelperFunctionsMixin.js'
import { keys, isEmpty } from 'ramda'

export default {
  name: 'CustomFilters',
  mixins: [translationInfoHelperFunctionsMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
    translationInfo: {
      type: Object,
      required: false,
    },
    validations: {
      type: Object,
      required: true,
    },
    showTranslations: Boolean,
  },
  data() {
    return {
      errorMessage: 'This field is required',
    }
  },
  methods: {
    addCustomFilter() {
      const filterKeys = Object.keys(this.filters)
      const lastFilterKey = filterKeys.length > 0 ? extractNumberFromString(filterKeys.pop()) : 0

      const element = {
        name: '',
        slug: '',
        question: '',
        rows: {
          'new-1': {
            active: true,
            name: '',
            slug: '',
          },
        },
      }
      Vue.set(this.filters, 'new-' + (lastFilterKey + 1), element)
      this.addNewElementTranslationInfoObject({
        translationInfo: this.translationInfo,
        key: lastFilterKey,
        elementToBeAdded: element,
        translationsProperty: 'Filters',
      })
    },
    removeCustomFilter(key) {
      this.$delete(this.filters, key)
      this.removeElementTranslationInfoObject({
        self: this,
        translationInfo: this.translationInfo,
        key: key,
        translationsProperty: 'Filters',
      })
    },
    addCustomFilterRow(filterKey) {
      const filterRowKeys = Object.keys(this.filters[filterKey].rows)
      const lastFilterRowKey =
        filterRowKeys.length > 0 ? extractNumberFromString(filterRowKeys.pop()) : 0

      const row = {
        active: true,
        name: '',
        slug: '',
      }
      Vue.set(this.filters[filterKey].rows, 'new-' + (lastFilterRowKey + 1), row)
      this.addNewElementTranslationInfoObject({
        translationInfo: this.translationInfo,
        key: filterKey,
        elementToBeAdded: row,
        translationsProperty: 'Filters',
        newChildElementToBeAdded: {
          key: lastFilterRowKey,
          childKeyName: 'rows',
        },
      })
    },
    removeCustomFilterRow(filterKey, rowKey) {
      if (rowKey === '1' || rowKey === 'new-1') return
      this.$delete(this.filters[filterKey].rows, rowKey)
      this.removeElementTranslationInfoObject({
        self: this,
        translationInfo: this.translationInfo,
        key: filterKey,
        translationsProperty: 'Filters',
        childElementToBeRemoved: {
          key: rowKey,
          childKeyName: 'rows',
        },
      })
    },
    customFilterError(filterKey) {
      return (this.$v.filters.$each[filterKey] && this.$v.filters.$each[filterKey].name.$error) ||
        (this.validations.$each?.[filterKey] && this.validations.$each?.[filterKey].name.$error)
        ? this.errorMessage
        : null
    },
    customFilterRowError(filterKey, rowKey) {
      return (this.$v.filters.$each[filterKey].rows.$each[rowKey] &&
        this.$v.filters.$each[filterKey].rows.$each[rowKey].name.$error) ||
        (this.validations.$each?.[filterKey].rows.$each[rowKey] &&
          this.validations.$each?.[filterKey].rows.$each[rowKey].name.$error)
        ? this.errorMessage
        : null
    },
    updateShowForTranslationInfoObject(filterKey, rowKey, active) {
      if (isEmpty(this.translationInfo.AvailableCultures)) return false
      const translationInfo = this.translationInfo
      this.translationInfo.AvailableCultures.forEach(function(availableCulture) {
        translationInfo.Translations[availableCulture.Name].Filters[filterKey].rows[
          rowKey
        ].active = active
      })
    },
  },
  validations() {
    return {
      filters: {
        $each: {
          name: { required },
          rows: {
            $each: {
              name: { required },
            },
          },
        },
      },
    }
  },
}
</script>

<style scoped lang="stylus">
.heading
  display flex
  justify-content space-between
  align-items center

.custom-filters-filter
  border-bottom 1px solid #F1F3F6
  margin-bottom 30px
  padding-bottom 10px

.custom-filters-filter-container
  display flex
  align-items center

.custom-filters-filter-label
    width 95%

.custom-filters-delete
    margin 0 auto
    cursor pointer
    padding-left 20px

.custom-filters-filter-row
    display flex
    align-items center

.custom-filters-checkbox
    width 100px
    padding-top 20px

.custom-filters-row-button
    margin 0 auto
    cursor pointer
    padding-left 20px

.disabled
    opacity 0.4
    cursor not-allowed
</style>
